import { useParams, useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '../../utils/format';
import { useExpenseSheet, useSubmitExpenseSheet, useCancelExpenseSheet, useReopenExpenseSheet, useReimburseExpenseSheet, useDeleteExpenseSheet } from '../../hooks/useExpenses';
import { useAuth } from '../../services/authContext';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import ReceiptList from '../../components/domain/ReceiptList';
import { useAuditTrail } from '../../hooks/useAuditTrail';
import { StateHistory } from '../../components/workflow/StateHistory';
import { useState } from 'react';

export default function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const sheetId = id ? parseInt(id, 10) : undefined;
  const { data: auditTrail } = useAuditTrail('ExpenseSheet', sheetId ?? 0);
  const { data: sheet, isLoading, error } = useExpenseSheet(sheetId);

  const submitMutation = useSubmitExpenseSheet();
  const cancelMutation = useCancelExpenseSheet();
  const reopenMutation = useReopenExpenseSheet();
  const reimburseMutation = useReimburseExpenseSheet();
  const deleteMutation = useDeleteExpenseSheet();

  const [confirmAction, setConfirmAction] = useState<'submit' | 'cancel' | 'reopen' | 'reimburse' | 'delete' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAction = async () => {
    if (!sheetId || !confirmAction) return;
    setActionError(null);

    try {
      switch (confirmAction) {
        case 'submit':
          await submitMutation.mutateAsync(sheetId);
          break;
        case 'cancel':
          await cancelMutation.mutateAsync(sheetId);
          break;
        case 'reopen':
          await reopenMutation.mutateAsync(sheetId);
          break;
        case 'reimburse':
          await reimburseMutation.mutateAsync(sheetId);
          break;
        case 'delete':
          await deleteMutation.mutateAsync(sheetId);
          navigate('/expenses');
          break;
      }
      setConfirmAction(null);
    } catch (err) {
      setActionError((err as Error).message || 'Action failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error || !sheet) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error ? (error as Error).message : 'Expense sheet not found'}</p>
        <Button variant="secondary" className="mt-2" onClick={() => navigate('/expenses')}>
          Back to Expenses
        </Button>
      </div>
    );
  }

  const actionLabels: Record<string, string> = {
    submit: 'Submit for Approval',
    cancel: 'Cancel Expense Sheet',
    reopen: 'Reopen Expense Sheet',
    reimburse: 'Mark as Reimbursed',
    delete: 'Delete Expense Sheet',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/expenses')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
            &larr; Back to Expenses
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {sheet.description || 'Expense Sheet'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge state={sheet.state} />
          {sheet.isReimbursed && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Reimbursed
            </span>
          )}
        </div>
      </div>

      {/* Details Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Project</p>
            <p className="font-medium text-gray-900">{sheet.project?.name || 'No project'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Receipts</p>
            <p className="font-medium text-gray-900">{sheet.receiptCount}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Amount</p>
            <p className="font-medium text-gray-900">{sheet.totalAmount.toFixed(2)} CZK</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="font-medium text-gray-900">
              {sheet.createdAt ? formatDisplayDate(sheet.createdAt) : 'N/A'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100">
          {sheet.state === 'open' && (
            <>
              <Button onClick={() => setConfirmAction('submit')} disabled={sheet.receiptCount === 0}>
                Submit for Approval
              </Button>
              <Button variant="danger" onClick={() => setConfirmAction('delete')}>
                Delete
              </Button>
            </>
          )}
          {(sheet.state === 'submitted' || sheet.state === 'approved') && (
            <Button variant="secondary" onClick={() => setConfirmAction('cancel')}>
              Cancel
            </Button>
          )}
          {sheet.state === 'cancelled' && (
            <Button variant="danger" onClick={() => setConfirmAction('delete')}>
              Delete
            </Button>
          )}
          {(sheet.state === 'rejected' || sheet.state === 'cancelled') && (
            <Button variant="secondary" onClick={() => setConfirmAction('reopen')}>
              Reopen
            </Button>
          )}
          {isAdmin && sheet.state === 'approved' && !sheet.isReimbursed && (
            <Button onClick={() => setConfirmAction('reimburse')}>
              Mark as Reimbursed
            </Button>
          )}
        </div>
      </div>

      {/* Receipts */}
      <ReceiptList sheetId={sheet.id} receipts={sheet.receipts} editable={sheet.state === 'open'} />

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Action Confirmation */}
      <ConfirmModal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleAction}
        title={confirmAction ? actionLabels[confirmAction] ?? '' : ''}
        message={
          confirmAction === 'submit'
            ? 'Are you sure you want to submit this expense sheet for approval?'
            : confirmAction === 'cancel'
            ? 'Are you sure you want to cancel this expense sheet?'
            : confirmAction === 'reimburse'
            ? 'Are you sure you want to mark this expense sheet as reimbursed?'
            : confirmAction === 'delete'
            ? 'Are you sure you want to delete this expense sheet? This action cannot be undone.'
            : 'Are you sure you want to reopen this expense sheet?'
        }
        confirmText={
          confirmAction === 'cancel'
            ? 'Cancel Sheet'
            : confirmAction === 'reimburse'
            ? 'Mark as Reimbursed'
            : confirmAction === 'delete'
            ? 'Delete'
            : 'Confirm'
        }
        variant={confirmAction === 'cancel' || confirmAction === 'delete' ? 'danger' : undefined}
        isLoading={submitMutation.isPending || cancelMutation.isPending || reopenMutation.isPending || reimburseMutation.isPending || deleteMutation.isPending}
      />

      {/* History */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">History</h3>
        <StateHistory history={auditTrail?.data ?? []} />
      </div>
    </div>
  );
}
